import { Location } from "@angular/common";
import { Component, ElementRef, computed, inject, signal, viewChild, viewChildren } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ButtonModule } from "primeng/button";
import { SkeletonModule } from "primeng/skeleton";
import { Assertion, AssertionIssue, ImageType, Media } from "../../../../core/database/db";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { HorizontalButton, HorizontalButtonGroupComponent } from "../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { IconAction, IconActionGroupComponent } from "../../../../shared/components/icon-action-group/icon-action-group.component";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../media/services/image-compression-pipeline.service";
import { UNASSIGNED_AREA } from "../../../areas/models/AreaDTO";
import { resolveLastUsedRoomFromIssues } from "../../../jobIssues/utils/last-used-room.util";
import { BqInspectionSingleAreaDataService, ChecklistResponse } from "../../services/bq-inspection-single-area-data.service";
import { DummyBqInspectionDataService } from "../../services/dummy-bq-inspection-data-service";
import { AddAreaSubmit } from "../job-areas/add-area/add-area-dialog.component";
import { ChecklistIssueSheetComponent, ChecklistIssueSheetMode, ChecklistIssueSubmit } from "./components/checklist-issue-sheet/checklist-issue-sheet.component";
import { ChecklistIssuePhotos, ChecklistIssuesSheetComponent, ChecklistLoggedIssue } from "./components/checklist-issues-sheet/checklist-issues-sheet.component";
import { ChecklistLooksGoodSheetComponent, ChecklistLooksGoodSubmit } from "./components/checklist-looks-good-sheet/checklist-looks-good-sheet.component";
import { ChecklistSectionsSheetComponent } from "./components/checklist-sections-sheet/checklist-sections-sheet.component";
import { BackgroundRefreshIndicatorComponent } from "../../../../shared/components/background-refresh-indicator/background-refresh-indicator.component";
import ScheduleDetailHeader from "../shared/schedule-detail-header/schedule-detail-header.component";

/** Scroll distance (px) past which the lot/step title block collapses away. */
const HEADER_COLLAPSE_THRESHOLD = 8;

/** Saves are local-first and usually instant, so only surface a spinner once one is visibly slow. */
const ANSWER_SPINNER_DELAY_MS = 750;

/** A single checklist item (assertion) plus the answer the CM has already given it, if any. */
export interface ChecklistItem {
  assertion: Assertion;
  response: ChecklistResponse | null;
  answerButtons: HorizontalButton[];
  /** Trailing icon controls shown once the item is answered; empty while it is unanswered. */
  answeredActions: IconAction[];
  /** Issues logged against the item; only ever populated for Needs Attention. */
  issueCount: number;
  /** Photos attached to the item's answer, across every logged issue for Needs Attention. */
  photoCount: number;
}

/** A named group of checklist items (a component within the step's single area). */
export interface ChecklistSection {
  id: number;
  name: string;
  items: ChecklistItem[];
  answeredCount: number;
  totalCount: number;
}

/**
 * Sectioned checklist for the BQ steps that inspect a single area (BQ2, BQ3, BQ4).
 *
 * The step's area holds one component per named section (e.g. Structural, Safety, PCS) and each
 * component's assertions are the checklist items. Every item takes one of three answers - Pass,
 * Needs Attention or N/A - which is persisted through the BQ inspection data service.
 */
@Component({
  selector: "app-job-single-area-checklist",
  standalone: true,
  imports: [SkeletonModule, ButtonModule, HorizontalButtonGroupComponent, IconActionGroupComponent, ChecklistIssueSheetComponent, ChecklistIssuesSheetComponent, ChecklistLooksGoodSheetComponent, ChecklistSectionsSheetComponent, BackgroundRefreshIndicatorComponent, ScheduleDetailHeader],
  templateUrl: "./job-single-area-checklist.component.html",
  styleUrl: "./job-single-area-checklist.component.scss",
  providers: [BqStepDataServiceFactory]
})
export class JobSingleAreaChecklistComponent {
  private readonly bqStepDataServiceFactory = inject(BqStepDataServiceFactory);
  private readonly toastService = inject(UiToastService);
  private readonly logger = inject(LoggerService);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly imageCompressionPipelineService = inject(ImageCompressionPipelineService);

  private readonly issueSheet = viewChild(ChecklistIssueSheetComponent);
  private readonly content = viewChild<ElementRef<HTMLElement>>("content");
  private readonly sectionElements = viewChildren<ElementRef<HTMLElement>>("sectionEl");

  readonly ChecklistResponse = ChecklistResponse;

  dataLoaded = signal<boolean>(false);
  processingAssertionId = signal<number | null>(null);
  updatingResponse = signal<ChecklistResponse | null>(null);
  issueSheetOpen = signal<boolean>(false);
  issueSheetAssertion = signal<Assertion | null>(null);
  issueSheetAssertionText = computed(() => this.issueSheetAssertion()?.text ?? "");
  issueSheetMode = signal<ChecklistIssueSheetMode>("create");
  editingIssue = signal<AssertionIssue | null>(null);
  savingIssue = signal<boolean>(false);
  looksGoodSheetOpen = signal<boolean>(false);
  looksGoodAssertion = signal<Assertion | null>(null);
  savingLooksGood = signal<boolean>(false);
  clearingResponse = signal<boolean>(false);
  issuesSheetOpen = signal<boolean>(false);
  issuesSheetAssertion = signal<Assertion | null>(null);
  savingRoom = signal<boolean>(false);
  /** Identifier of the room just created from the issue sheet, so that sheet can select it. */
  createdRoomIdentifier = signal<string | undefined>(undefined);
  headerCollapsed = signal<boolean>(false);
  pinnedSectionId = signal<number | null>(null);
  showSectionsBottomSheet = signal<boolean>(false);

  private bqStepDataService = signal<BqInspectionSingleAreaDataService>(new DummyBqInspectionDataService(this.toastService));

  issueSheetParentIdentifier = computed(() => this.editingIssue()?.identifier ?? "");
  issueSheetInitialNotes = computed(() => this.editingIssue()?.text ?? "");
  issueSheetInitialLotAreaIdentifier = computed(() => {
    if (this.issueSheetMode() === "edit") {
      return this.editingIssue()?.lotAreaIdentifier;
    }

    const resolved = resolveLastUsedRoomFromIssues(this.bqStepDataService().assertionIssues() ?? []);
    if (!resolved) {
      return undefined;
    }

    return this.availableRooms().some(room => room.identifier === resolved) ? resolved : undefined;
  });
  issueSheetInitialMedia = computed(() => {
    const issue = this.editingIssue();
    return issue ? this.bqStepDataService().getMediaForIssue(issue.identifier) : [];
  });

  looksGoodAssertionText = computed(() => this.looksGoodAssertion()?.text ?? "");
  looksGoodParentIdentifier = computed(() => this.looksGoodAssertion()?.identifier ?? "");
  looksGoodImageType = computed(() => this.bqStepDataService().looksGoodImageType());
  looksGoodNotes = computed(() => {
    const assertion = this.looksGoodAssertion();
    return assertion ? this.resolveNote(assertion) : "";
  });
  looksGoodMedia = computed(() => {
    const assertion = this.looksGoodAssertion();
    return assertion ? this.bqStepDataService().getLooksGoodMediaForAssertion(assertion.identifier) : [];
  });

  issuesSheetAssertionText = computed(() => this.issuesSheetAssertion()?.text ?? "");
  loggedIssues = computed<ChecklistLoggedIssue[]>(() => {
    const assertion = this.issuesSheetAssertion();
    if (!assertion) {
      return [];
    }

    return this.bqStepDataService()
      .getIssuesForAssertion(assertion.identifier)
      .map(issue => ({ issue, media: this.bqStepDataService().getMediaForIssue(issue.identifier) }));
  });

  area = computed(() => (this.bqStepDataService().areas() ?? [])[0]);
  availableRooms = computed(() => this.bqStepDataService().availableRooms() ?? []);
  /** Only the steps that load lot rooms (BQ3/BQ4) let the CM create one mid-inspection. */
  canAddRoom = computed(() => this.bqStepDataService().availableRooms() !== undefined);
  areasConfig = computed(() => this.bqStepDataService().areasConfig());
  zoneOptions = computed(() => this.bqStepDataService().lotZones());
  title = computed(() => this.area()?.name ?? "");
  subtitle = computed(() => {
    const lot = this.bqStepDataService().lot();
    return lot ? `Lot ${lot.lotBlock} - ${lot.streetAddress}` : "";
  });
  bqStep = computed(() => this.bqStepDataService().job()?.buildQualityStep ?? 0);
  isRefreshingInBackground = computed(() => this.bqStepDataService()?.isRefreshingInBackground() ?? false);

  sections = computed<ChecklistSection[]>(() => {
    const areaId = this.area()?.id;
    if (!areaId) {
      return [];
    }

    const assertions = this.bqStepDataService().assertions() ?? [];

    const issuesByAssertionIdentifier = new Map<string, AssertionIssue[]>();
    (this.bqStepDataService().assertionIssues() ?? [])
      .filter(issue => issue.deleted !== 1)
      .forEach(issue => {
        const existing = issuesByAssertionIdentifier.get(issue.assertionIdentifier) ?? [];
        issuesByAssertionIdentifier.set(issue.assertionIdentifier, [...existing, issue]);
      });

    const photoCountByParentIdentifier = new Map<string, number>();
    (this.bqStepDataService().media() ?? [])
      .filter(media => media.deleted !== 1)
      .forEach(media => {
        photoCountByParentIdentifier.set(media.parentIdentifier, (photoCountByParentIdentifier.get(media.parentIdentifier) ?? 0) + 1);
      });

    const openIssueAssertionIdentifiers = new Set(issuesByAssertionIdentifier.keys());
    const isCcmInspection = this.bqStepDataService().isCcmInspection();

    return (this.bqStepDataService().components() ?? [])
      .filter(component => component.areaId === areaId)
      // Sections and their items are ordered by insertion so they match the step's checklist definition.
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map(component => {
        const items = assertions
          .filter(assertion => assertion.componentId === component.id)
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          .map(assertion => {
            const issues = issuesByAssertionIdentifier.get(assertion.identifier) ?? [];
            const response = this.resolveResponse(assertion, openIssueAssertionIdentifiers, isCcmInspection);
            const photoCount = response === ChecklistResponse.NeedsAttention
              ? issues.reduce((total, issue) => total + (photoCountByParentIdentifier.get(issue.identifier) ?? 0), 0)
              : photoCountByParentIdentifier.get(assertion.identifier) ?? 0;

            return {
              assertion,
              response,
              answerButtons: this.buildAnswerButtons(assertion),
              answeredActions: this.buildAnsweredActions(assertion, response, issues.length, photoCount),
              issueCount: issues.length,
              photoCount
            };
          });

        return {
          id: component.id!,
          name: component.name,
          items,
          answeredCount: items.filter(item => item.response !== null).length,
          totalCount: items.length
        };
      })
      .filter(section => section.totalCount > 0);
  });

  /** A section counts as complete only once every one of its items has an answer. */
  completedSectionCount = computed(() => this.sections().filter(section => section.answeredCount === section.totalCount).length);
  sectionCount = computed(() => this.sections().length);
  allSectionsComplete = computed(() => this.sectionCount() > 0 && this.completedSectionCount() === this.sectionCount());
  footerLabel = computed(() => (this.allSectionsComplete() ? "Submit" : "View Sections"));

  async ngOnInit(): Promise<void> {
    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService<BqInspectionSingleAreaDataService>(bqStep));

    if (!this.area()) {
      this.logger.error(`[JobSingleAreaChecklistComponent] No area scoped to ${bqStep} was loaded for this job.`);
      this.toastService.error(`No checklist was found for ${bqStep}. Please try again.`);
      this.goBack();
      return;
    }

    this.dataLoaded.set(true);
  }

  onContentScroll(): void {
    const element = this.content()?.nativeElement;
    if (!element) {
      return;
    }

    this.headerCollapsed.set(element.scrollTop > HEADER_COLLAPSE_THRESHOLD);
    this.pinnedSectionId.set(this.resolvePinnedSectionId(element));
  }

  isPinnedSection(sectionId: number): boolean {
    return this.headerCollapsed() && this.pinnedSectionId() === sectionId;
  }

  /** The pinned header belongs to the last section whose top has scrolled past the viewport top. */
  private resolvePinnedSectionId(container: HTMLElement): number | null {
    const containerTop = container.getBoundingClientRect().top;
    let pinnedId: number | null = null;

    for (const section of this.sectionElements()) {
      const element = section.nativeElement;
      if (element.getBoundingClientRect().top - containerTop > 1) {
        break;
      }
      pinnedId = Number(element.dataset["sectionId"]);
    }

    return pinnedId;
  }

  onFooterAction(): void {
    if (this.allSectionsComplete()) {
      this.router.navigate(["submission"], { relativeTo: this.route.parent });
      return;
    }

    this.onShowSectionsBottomSheet();
  }

  onShowSectionsBottomSheet(): void {
    this.showSectionsBottomSheet.set(true);
  }

  onSectionSelected(sectionId: number): void {
    this.showSectionsBottomSheet.set(false);

    const section = this.sectionElements().find(element => Number(element.nativeElement.dataset["sectionId"]) === sectionId);
    section?.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  goBack(): void {
    this.location.back();
  }

  private buildAnswerButtons(assertion: Assertion): HorizontalButton[] {
    const itemText = this.toPlainText(assertion.text);

    return [
      {
        label: "",
        icon: "bqa-answer-icon bqa-answer-icon-check",
        ariaLabel: `Mark ${itemText} as Pass`,
        styleClass: "bqa-answer-btn bqa-answer-btn-pass",
        loading: this.isAnswerSaving(assertion, ChecklistResponse.Pass),
        action: () => this.respond(assertion, ChecklistResponse.Pass),
        testId: `single-area-checklist-btn-pass-${assertion.id}`
      },
      {
        label: "",
        icon: "bqa-answer-icon bqa-answer-icon-warning",
        ariaLabel: `Mark ${itemText} as Needs Attention`,
        styleClass: "bqa-answer-btn bqa-answer-btn-attention",
        loading: this.isAnswerSaving(assertion, ChecklistResponse.NeedsAttention),
        action: () => this.respond(assertion, ChecklistResponse.NeedsAttention),
        testId: `single-area-checklist-btn-attention-${assertion.id}`
      },
      {
        label: "N/A",
        ariaLabel: `Mark ${itemText} as not applicable`,
        styleClass: "bqa-answer-btn bqa-answer-btn-na",
        loading: this.isAnswerSaving(assertion, ChecklistResponse.NotApplicable),
        action: () => this.respond(assertion, ChecklistResponse.NotApplicable),
        testId: `single-area-checklist-btn-na-${assertion.id}`
      }
    ];
  }

  /** Keeps the spinner on the one button the CM pressed rather than the whole trio. */
  private isAnswerSaving(assertion: Assertion, response: ChecklistResponse): boolean {
    return this.processingAssertionId() === assertion.id && this.updatingResponse() === response;
  }

  /** Trailing controls for an answered row: what they do depends on which answer was given. */
  private buildAnsweredActions(assertion: Assertion, response: ChecklistResponse | null, issueCount: number, photoCount: number): IconAction[] {
    switch (response) {
      case ChecklistResponse.Pass: {
        // A passed item carries at most one note, so the badge is only ever absent or 1.
        const noteCount = this.resolveNote(assertion).trim() ? 1 : 0;

        return [
          {
            icon: "pi pi-comment",
            ariaLabel: noteCount ? "Edit notes and photos" : "Add notes and photos",
            count: noteCount,
            action: () => this.openLooksGoodSheet(assertion),
            testId: `single-area-checklist-btn-note-${assertion.id}`
          },
          {
            capture: "library",
            ariaLabel: "Add photos to this item",
            count: photoCount,
            disabled: this.savingLooksGood(),
            inputId: `single-area-checklist-photo-${assertion.id}`,
            filesSelected: files => this.onLooksGoodPhotosSelected(assertion, files),
            testId: `single-area-checklist-btn-photo-${assertion.id}`
          }
        ];
      }

      // Static glyphs: the whole row is the tap target, so these must not swallow the click.
      case ChecklistResponse.NeedsAttention:
        return [
          {
            icon: "pi pi-comment",
            ariaLabel: `${issueCount} logged issues`,
            count: issueCount,
            testId: `single-area-checklist-issue-count-${assertion.id}`
          },
          {
            icon: "pi pi-camera",
            ariaLabel: `${photoCount} photos`,
            count: photoCount,
            testId: `single-area-checklist-photo-count-${assertion.id}`
          }
        ];

      case ChecklistResponse.NotApplicable:
        return [
          {
            icon: "pi pi-replay",
            ariaLabel: "Undo N/A",
            disabled: this.clearingResponse(),
            action: () => this.onClearResponse(assertion),
            testId: `single-area-checklist-btn-undo-na-${assertion.id}`
          }
        ];

      default:
        return [];
    }
  }

  private async respond(assertion: Assertion, response: ChecklistResponse): Promise<void> {
    if (!assertion.id) {
      this.logger.error("[JobSingleAreaChecklistComponent] Cannot record a response for an assertion without an ID.");
      this.toastService.error("Unable to save your response. Please try again.");
      return;
    }

    if (this.processingAssertionId() !== null) {
      return;
    }

    // Needs Attention needs photos and notes before there is anything to persist.
    if (response === ChecklistResponse.NeedsAttention) {
      this.editingIssue.set(null);
      this.issueSheetMode.set("create");
      this.issueSheetAssertion.set(assertion);
      this.issueSheetOpen.set(true);
      return;
    }

    this.processingAssertionId.set(assertion.id);
    const spinnerTimeout = window.setTimeout(() => this.updatingResponse.set(response), ANSWER_SPINNER_DELAY_MS);

    try {
      await this.bqStepDataService().recordChecklistResponse(assertion, response, this.area().identifier);
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to save your response. Please try again.");
    } finally {
      clearTimeout(spinnerTimeout);
      this.updatingResponse.set(null);
      this.processingAssertionId.set(null);
    }
  }

  /** An answered row reopens whichever sheet owns its answer; N/A has nothing to edit, only to undo. */
  onAnsweredItemClick(item: ChecklistItem): void {
    if (item.response === ChecklistResponse.Pass) {
      this.openLooksGoodSheet(item.assertion);
      return;
    }

    if (item.response === ChecklistResponse.NeedsAttention) {
      this.openIssuesSheet(item.assertion);
    }
  }

  openLooksGoodSheet(assertion: Assertion): void {
    this.looksGoodAssertion.set(assertion);
    this.looksGoodSheetOpen.set(true);
  }

  async onLooksGoodSubmitted(details: ChecklistLooksGoodSubmit): Promise<void> {
    const assertion = this.looksGoodAssertion();
    if (!assertion) {
      return;
    }

    this.savingLooksGood.set(true);

    try {
      await this.bqStepDataService().saveLooksGoodAssertionDetail(assertion, details.notes, details.media);
      this.closeLooksGoodSheet();
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to save your response. Please try again.");
    } finally {
      this.savingLooksGood.set(false);
    }
  }

  /** "Clear response" from the edit sheet, and the Undo action on an N/A row, are the same reset. */
  async onClearResponse(assertion: Assertion | null): Promise<void> {
    if (!assertion || this.clearingResponse()) {
      return;
    }

    this.clearingResponse.set(true);

    try {
      await this.bqStepDataService().clearChecklistResponse(assertion);
      this.closeLooksGoodSheet();
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to clear your response. Please try again.");
    } finally {
      this.clearingResponse.set(false);
    }
  }

  closeLooksGoodSheet(): void {
    this.looksGoodSheetOpen.set(false);
    this.looksGoodAssertion.set(null);
  }

  /** Camera icon on a passed row: the photo goes straight onto the item without opening the sheet. */
  async onLooksGoodPhotosSelected(assertion: Assertion, files: File[]): Promise<void> {
    if (this.savingLooksGood()) {
      return;
    }

    this.savingLooksGood.set(true);

    try {
      const media = await this.capturePhotos(files, assertion.identifier, this.bqStepDataService().looksGoodImageType());
      await this.bqStepDataService().saveLooksGoodAssertionDetail(assertion, this.resolveNote(assertion), media);
      this.toastService.success("Photo Saved");
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to save your photo. Please try again.");
    } finally {
      this.savingLooksGood.set(false);
    }
  }

  openIssuesSheet(assertion: Assertion): void {
    this.issuesSheetAssertion.set(assertion);
    this.issuesSheetOpen.set(true);
  }

  closeIssuesSheet(): void {
    this.issuesSheetOpen.set(false);
    this.issuesSheetAssertion.set(null);
  }

  onEditIssueRequested(issue: AssertionIssue): void {
    const assertion = this.issuesSheetAssertion();
    this.issuesSheetOpen.set(false);
    this.editingIssue.set(issue);
    this.issueSheetMode.set("edit");
    this.issueSheetAssertion.set(assertion);
    this.issueSheetOpen.set(true);
  }

  async onDeleteIssueRequested(issue: AssertionIssue): Promise<void> {
    if (this.savingIssue()) {
      return;
    }

    this.savingIssue.set(true);

    try {
      await this.bqStepDataService().deleteIssueForAssertion(issue);
      this.toastService.success("Issue Deleted");

      // Removing the last issue takes the item back to unanswered, so the sheet has nothing left to show.
      if (this.loggedIssues().length === 0) {
        this.closeIssuesSheet();
      }
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to delete the issue. Please try again.");
    } finally {
      this.savingIssue.set(false);
    }
  }

  /** Camera on a logged issue card: the photo is appended to that issue without opening the edit form. */
  async onIssuePhotosSelected(details: ChecklistIssuePhotos): Promise<void> {
    if (this.savingIssue()) {
      return;
    }

    this.savingIssue.set(true);

    try {
      const media = await this.capturePhotos(details.files, details.issue.identifier, ImageType.Issue);
      await this.bqStepDataService().updateChecklistIssue(details.issue, details.issue.text, media);
      this.toastService.success("Photo Saved");
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to save your photo. Please try again.");
    } finally {
      this.savingIssue.set(false);
    }
  }

  /** Delete raised from the edit sheet; drops back to the list unless that was the item's last issue. */
  async onEditedIssueDeleted(): Promise<void> {
    const issue = this.editingIssue();
    if (!issue) {
      return;
    }

    this.closeIssueSheet();
    await this.onDeleteIssueRequested(issue);

    // onDeleteIssueRequested clears the assertion once the last issue is gone.
    if (this.issuesSheetAssertion()) {
      this.issuesSheetOpen.set(true);
    }
  }

  onAddAnotherIssue(): void {
    const assertion = this.issuesSheetAssertion();
    if (!assertion) {
      return;
    }

    this.issuesSheetOpen.set(false);
    this.editingIssue.set(null);
    this.issueSheetMode.set("create");
    this.issueSheetAssertion.set(assertion);
    this.issueSheetOpen.set(true);
  }

  async onIssueSubmitted(details: ChecklistIssueSubmit): Promise<void> {
    const assertion = this.issueSheetAssertion();
    if (!assertion) {
      return;
    }

    this.savingIssue.set(true);

    try {
      // Unassigned is an explicit choice in the sheet, but is stored as no room at all.
      const lotAreaIdentifier = details.lotAreaIdentifier === UNASSIGNED_AREA.identifier
        ? undefined
        : (details.lotAreaIdentifier ?? this.area().identifier);

      const issueBeingEdited = this.editingIssue();
      if (this.issueSheetMode() === "edit" && issueBeingEdited) {
        await this.bqStepDataService().updateChecklistIssue(issueBeingEdited, details.notes, details.media, lotAreaIdentifier);
        this.toastService.success("Issue Updated");
        this.closeIssueSheet();
        this.openIssuesSheet(assertion);
        return;
      }

      await this.bqStepDataService().recordChecklistResponse(assertion, ChecklistResponse.NeedsAttention, lotAreaIdentifier!, {
        notes: details.notes,
        media: details.media
      });

      if (details.mode === "addAnother") {
        this.toastService.success("Issue Saved");
        const savedRoom = lotAreaIdentifier && this.availableRooms().some(room => room.identifier === lotAreaIdentifier)
          ? lotAreaIdentifier
          : null;
        this.issueSheet()?.resetForNextIssue(savedRoom);
        return;
      }

      this.closeIssueSheet();
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to save your response. Please try again.");
    } finally {
      this.savingIssue.set(false);
    }
  }

  closeIssueSheet(): void {
    this.issueSheetOpen.set(false);
    this.issueSheetAssertion.set(null);
    this.editingIssue.set(null);
    this.issueSheetMode.set("create");
    this.createdRoomIdentifier.set(undefined);
  }

  /** Creates a lot room from the issue sheet; it stays available to this job's later BQ steps. */
  async onRoomSubmitted(details: AddAreaSubmit): Promise<void> {
    this.savingRoom.set(true);

    try {
      const newRoom = await this.bqStepDataService().addNewRoomForIssueAssignment(details);
      this.createdRoomIdentifier.set(newRoom.identifier);
      this.toastService.success("Room Added");
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to add the room. Please try again.");
    } finally {
      this.savingRoom.set(false);
    }
  }

  /** Encodes and compresses freshly captured files into Media ready to hand to the data service. */
  private async capturePhotos(files: File[], parentIdentifier: string, imageType: ImageType): Promise<Media[]> {
    const encoded = await this.imageCompressionPipelineService.encode(files, parentIdentifier, imageType);

    let compressedRecords: MediaForCompression[] = encoded;
    this.imageCompressionPipelineService.compress(encoded, async compressed => {
      compressedRecords = compressed;
    });
    await this.imageCompressionPipelineService.waitForCompletion();

    return compressedRecords.map(record => {
      const { file: _file, ...rest } = record;
      return rest;
    });
  }

  /** The Looks Good note for whoever is inspecting; CMs and CCMs write to separate fields. */
  private resolveNote(assertion: Assertion): string {
    return (this.bqStepDataService().isCcmInspection() ? assertion.ccmNote : assertion.note) ?? "";
  }

  private resolveResponse(assertion: Assertion, openIssueAssertionIdentifiers: ReadonlySet<string>, isCcmInspection: boolean): ChecklistResponse | null {
    if (assertion.isApplicable === false) {
      return ChecklistResponse.NotApplicable;
    }

    if (openIssueAssertionIdentifiers.has(assertion.identifier)) {
      return ChecklistResponse.NeedsAttention;
    }

    if (isCcmInspection ? assertion.isCcmComplete : assertion.isComplete) {
      return ChecklistResponse.Pass;
    }

    return null;
  }

  /** Assertion text is stored with markup (e.g. <strong>), which has no place in an aria-label. */
  private toPlainText(text: string): string {
    let previous: string;
    let sanitized = text;

    do {
      previous = sanitized;
      sanitized = sanitized.replace(/<[^>]*>/g, "");
    } while (sanitized !== previous);

    return sanitized;
  }
}
